import React, { useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Table, UncontrolledTooltip } from 'reactstrap';
import { Link } from 'react-router-dom';
import { GET_TEAM_MEMBERS } from 'graphql/queries';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { TEAM_MEMBER_DELETE } from 'graphql/mutations';
import { graphqlLevelToAccessMap } from 'constants/global';
import useNotificationAlert from 'hooks/useNotificationAlert';
import useConfirmation from 'hooks/useConfirmation';
import { useSelector } from 'react-redux';
import { TEAM_MEMBER_FULL_PATH } from 'constants/paths';
import moment from 'moment';
import { graphqlVisibilityToVisibilityMap } from 'constants/global';
import { useSortBy, useTable } from 'react-table';
import usePersistentSortState from 'hooks/usePersistentSortState';

const perPage = 100;

const sortingKeys = {
  name: 'name',
  email: 'email',
  level: 'level',
  visibility: 'visibility',
  emailVerifiedAt: 'emailVerifiedAt',
  lastLoginAt: 'lastLoginAt'
};

function ListTeamMembers() {
  const { me } = useSelector((state) => state.auth);

  const { initialTableState, persistSortBy } = usePersistentSortState({
    storageKey: 'team_member_table_state',
    sortingKeys
  });

  const {
    loading: isLoadingDataTeamMembers,
    data: dataTeamMembers,
    refetch,
    networkStatus
  } = useQuery(GET_TEAM_MEMBERS, {
    variables: { first: perPage, page: 1 },
    notifyOnNetworkStatusChange: true
  });

  const [teamMemberDelete, { loading: isLoadingDestroy }] = useMutation(TEAM_MEMBER_DELETE);

  const { show: showNotificationAlert } = useNotificationAlert({
    place: 'br'
  });

  const { showConfirmation, hideConfirmation } = useConfirmation(isLoadingDestroy);

  const onDeleteTeamMember = (id) => {
    showConfirmation({
      title: 'Are you sure want to delete?',
      confirmTitle: 'Delete',
      onConfirm: async () => {
        try {
          await teamMemberDelete({ variables: { memberId: id } });
          hideConfirmation();
          showNotificationAlert({
            title: 'Success',
            message: 'You have deleted the team member successfully'
          });
          refetch();
        } catch (err) {
          switch (err.message) {
            case 'delete_yourself_not_permitted':
              showNotificationAlert({
                type: 'danger',
                title: 'Failed',
                message: 'Delete yourself is not permitted'
              });
              break;
            default:
              showNotificationAlert({
                type: 'danger',
                title: 'Failed',
                message: err.message
              });
              break;
          }
        } finally {
          hideConfirmation();
        }
      }
    });
  };

  const teamMembers = useMemo(() => dataTeamMembers?.getTeamMembers?.data || [], [dataTeamMembers]);

  const columns = useMemo(() => {
    const sortIcon = (column) =>
      column.isSorted ? (
        column.isSortedDesc ? (
          <span className="sorted-icon sorted-icon__desc" />
        ) : (
          <span className="sorted-icon sorted-icon__asc" />
        )
      ) : (
        <span className="sorted-icon" />
      );

    return [
      {
        Header: ({ column }) => <span className="heading-w-icon">Name {sortIcon(column)}</span>,
        id: 'name',
        accessor: (row) => `${row.firstName} ${row.lastName}`,
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`
      },
      {
        Header: ({ column }) => <span className="heading-w-icon">Email {sortIcon(column)}</span>,
        accessor: 'email'
      },
      {
        Header: ({ column }) =>
          column ? <span className="heading-w-icon">Access {sortIcon(column)}</span> : 'Access',
        accessor: 'level',
        Cell: ({ value }) => graphqlLevelToAccessMap[value]
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Visibility {sortIcon(column)}</span>
        ),
        accessor: 'visibility',
        Cell: ({ value }) => graphqlVisibilityToVisibilityMap[value]
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Date Joined {sortIcon(column)}</span>
        ),
        accessor: 'emailVerifiedAt',
        Cell: ({ value }) => (value ? moment(value).format('DD/MM/YYYY') : '')
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">Last Login {sortIcon(column)}</span>
        ),
        accessor: 'lastLoginAt',
        Cell: ({ value }) => (value ? moment(value).format('DD/MM/YYYY') : '')
      },
      {
        Header: () => '',
        id: 'action',
        disableSortBy: true,
        Cell: ({ row }) => {
          const data = row.original;

          return (
            <div className="table-actions">
              <div className="d-flex">
                {me?.level === 'ADMIN' && (
                  <>
                    <Link
                      className="table-action"
                      to={`${TEAM_MEMBER_FULL_PATH}/${data.id}/edit`}
                      id={`tooltip-edit-${data.id}`}>
                      <i className="fas fa-user-edit" />
                    </Link>
                    <UncontrolledTooltip delay={0} target={`tooltip-edit-${data.id}`}>
                      Edit Company Member Data
                    </UncontrolledTooltip>
                  </>
                )}
                {me?.level === 'ADMIN' && me?.id !== data.id && (
                  <>
                    <button
                      className="table-action table-action-delete"
                      id={`tooltip-delete-${data.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onDeleteTeamMember(data.id);
                      }}>
                      <i className="fas fa-trash" />
                    </button>
                    <UncontrolledTooltip delay={0} target={`tooltip-delete-${data.id}`}>
                      Delete Team Member
                    </UncontrolledTooltip>
                  </>
                )}
              </div>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: tableState
  } = useTable(
    {
      columns,
      data: teamMembers,
      disableSortRemove: true,
      initialState: initialTableState
    },
    useSortBy
  );

  useEffect(() => {
    persistSortBy(tableState.sortBy);
  }, [persistSortBy, tableState.sortBy]);

  return (
    <Row>
      <div className="col">
        <Card>
          <CardHeader className="border-0">
            <Row>
              <Col>
                <h3 className="mb-0 text-brand">List Joined Company Members</h3>
              </Col>
            </Row>
          </CardHeader>

          {(isLoadingDataTeamMembers || networkStatus === NetworkStatus.refetch) && (
            <CardBody>
              <p className="text-center">Loading...</p>
            </CardBody>
          )}
          {!isLoadingDataTeamMembers && dataTeamMembers && (
            <Table
              className="align-items-center table-flush"
              responsive
              striped
              {...getTableProps()}>
              <thead className="thead-light">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render('Header')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </Row>
  );
}

export default ListTeamMembers;
